import React, { useEffect, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { Button, Select, Spin } from "antd";
import Icons from "components/icons";
import { GET_SUBCONTRACTOR_CURSOR } from "graphql/query/subcontractor-gql";


// This picker only selects companies; it never creates companies or assignments.
const BookingCompanySelect = ({ value, onChange, excludedIds = [], disabled, text }) => {
    const client = useApolloClient();
    const [ search, setSearch ] = useState("");
    const [ retry, setRetry ] = useState(0);
    const [ options, setOptions ] = useState([]);
    const [ pageInfo, setPageInfo ] = useState(null);
    const [ loading, setLoading ] = useState(true);
    const [ error, setError ] = useState(false);
    const selectedOption = useRef(null);
    const generation = useRef(0);
    const pending = useRef(false);
    const loadPage = useRef(null);

    useEffect(() => {
        const requestGeneration = ++generation.current;
        let active = true;
        setOptions([]);
        setPageInfo(null);
        setError(false);
        setLoading(true);
        pending.current = false;

        const load = async (after) => {
            if (!active || pending.current) return;
            pending.current = true;
            setLoading(true);
            setError(false);
            try {
                const result = await client.query({
                    query: GET_SUBCONTRACTOR_CURSOR,
                    variables: { text: search, first: 25, after },
                    fetchPolicy: "no-cache",
                });
                if (!active || generation.current !== requestGeneration) return;
                const connection = result.data?.subcontractorsCursor;
                if (result.errors?.length || !Array.isArray(connection?.edges) ||
                    typeof connection?.pageInfo?.hasNextPage !== "boolean" ||
                    (connection.pageInfo.hasNextPage &&
                        (!connection.pageInfo.endCursor || connection.pageInfo.endCursor === after))) {
                    throw new Error("Invalid company search response");
                }
                const incoming = connection.edges.map(({ node }) => {
                    if (!node?.id || !node?.title) throw new Error("Invalid company search result");
                    return { value: String(node.id), label: node.title };
                });
                setOptions(previous => Array.from(new Map(
                    [ ...(after ? previous : []), ...incoming ].map(option => [ option.value, option ])
                ).values()));
                setPageInfo(connection.pageInfo);
            } catch (_) {
                if (active && generation.current === requestGeneration) setError(true);
            } finally {
                if (active && generation.current === requestGeneration) {
                    pending.current = false;
                    setLoading(false);
                }
            }
        };
        loadPage.current = load;
        const timer = setTimeout(() => load(), search ? 250 : 0);
        return () => {
            active = false;
            generation.current += 1;
            clearTimeout(timer);
        };
    }, [ client, search, retry ]);

    const excluded = new Set(excludedIds.map(String));
    const available = options.filter(option => !excluded.has(option.value));
    const currentOption = selectedOption.current;
    const displayOptions = value && currentOption?.value === String(value) &&
        !excluded.has(currentOption.value) && !available.some(option => option.value === currentOption.value)
        ? [ currentOption, ...available ] : available;

    return (
        <Select
            value={ value }
            disabled={ disabled }
            aria-label={ text("Company") }
            showSearch
            suffixIcon={ <Icons.Arrow /> }
            filterOption={ false }
            placeholder={ text("ChooseCompany") }
            searchValue={ search }
            onSearch={ nextSearch => {
                // Invalidate a previous search immediately, before the debounced request.
                if (nextSearch === search) return;
                generation.current += 1;
                setSearch(nextSearch);
            } }
            onChange={ (nextValue, option) => {
                selectedOption.current = { value: nextValue, label: option.label };
                onChange(nextValue);
                setSearch("");
            } }
            loading={ loading }
            options={ displayOptions }
            notFoundContent={ loading ? <Spin size="small" /> : text(error ? "CompanyLoadFailed" : "NoCompanies") }
            dropdownRender={ menu => (
                <>
                    { (!error || displayOptions.length > 0) && menu }
                    { error && <div className="booking-company-select__footer" role="alert">
                        <span>{ text("CompanyLoadFailed") }</span>
                        <Button className="ant-btn-secondary bg-light-gray" size="small" onMouseDown={ event => event.preventDefault() }
                            onClick={ () => pageInfo ? loadPage.current?.(pageInfo.endCursor) : setRetry(previous => previous + 1) }>
                            { text("Retry") }
                        </Button>
                    </div> }
                    { !error && pageInfo?.hasNextPage && <div className="booking-company-select__footer">
                        <Button className="ant-btn-secondary bg-light-gray" size="small" loading={ loading } onMouseDown={ event => event.preventDefault() }
                            onClick={ () => loadPage.current?.(pageInfo.endCursor) }>
                            { text("LoadMore") }
                        </Button>
                    </div> }
                </>
            ) }
        />
    );
};

export default BookingCompanySelect;
